import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { EventStatus } from "@/types/enums";
import { type EventDetail, getServiceSegments } from "@/types/event";
import type { PatientChart } from "@/types/patient";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";
import { chargeQueryOptions } from "@/api/provider/billing/charge-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
interface NextAppointment {
  title: string;
  start: Date;
  serviceNames: string[];
  providerName: string | null;
}
interface CallerEnrichment {
  nextAppointment: NextAppointment | null;
  isAppointmentsLoading: boolean;
  patientDetail: PatientChart | undefined;
  isPatientDetailLoading: boolean;
  sidebarCounts: PatientSidebarCounts | undefined;
  isSidebarCountsLoading: boolean;
  outstandingBalance: number;
  isChargesLoading: boolean;
}
export function useCallerEnrichment(patientId) {
  const $ = _c(35);
  const enabled = !!patientId;
  let t0;
  if ($[0] !== patientId) {
    t0 = patientQueryOptions.detail(patientId ?? "");
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enabled || $[3] !== t0) {
    t1 = {
      ...t0,
      enabled,
      staleTime: Infinity
    };
    $[2] = enabled;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: patientDetail,
    isLoading: isPatientDetailLoading
  } = useQuery(t1);
  let t2;
  if ($[5] !== patientId) {
    t2 = eventQueryOptions.searchPatientEvents(patientId ?? "", "");
    $[5] = patientId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== enabled || $[8] !== t2) {
    t3 = {
      ...t2,
      enabled,
      staleTime: Infinity
    };
    $[7] = enabled;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: events,
    isLoading: isAppointmentsLoading
  } = useQuery(t3);
  let t4;
  if ($[10] !== patientId) {
    t4 = patientQueryOptions.sidebarCounts(patientId ?? "");
    $[10] = patientId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== enabled || $[13] !== t4) {
    t5 = {
      ...t4,
      enabled,
      staleTime: Infinity
    };
    $[12] = enabled;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data: sidebarCounts,
    isLoading: isSidebarCountsLoading
  } = useQuery(t5);
  let t6;
  if ($[15] !== patientId) {
    t6 = chargeQueryOptions.table(patientId ?? "");
    $[15] = patientId;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== enabled || $[18] !== t6) {
    t7 = {
      ...t6,
      enabled,
      staleTime: Infinity
    };
    $[17] = enabled;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data: charges,
    isLoading: isChargesLoading
  } = useQuery(t7);
  let t8;
  bb0: {
    if (!charges?.length) {
      t8 = 0;
      break bb0;
    }
    let t9;
    if ($[20] !== charges) {
      t9 = charges.reduce(_temp, 0);
      $[20] = charges;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = t9;
  }
  const outstandingBalance = t8;
  let t9;
  if ($[22] !== events) {
    bb1: {
      if (!events?.length) {
        t9 = null;
        break bb1;
      }
      let t10;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = new Date();
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      const now = t10;
      let t11;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = e => new Date(e.start) > now && e.status !== EventStatus.CANCELLED;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      const upcoming = events.filter(t11).sort(_temp2);
      const next = upcoming[0];
      if (!next) {
        t9 = null;
        break bb1;
      }
      const serviceSegments = getServiceSegments(next.segments ?? []);
      t9 = {
        title: next.title ?? "",
        start: new Date(next.start),
        serviceNames: serviceSegments.map(_temp3),
        providerName: serviceSegments.find(_temp4)?.provider?.firstName ?? null
      };
    }
    $[22] = events;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const nextAppointment = t9;
  const t10 = enabled && isAppointmentsLoading;
  const t11 = enabled && isPatientDetailLoading;
  const t12 = enabled && isSidebarCountsLoading;
  const t13 = enabled && isChargesLoading;
  let t14;
  if ($[26] !== nextAppointment || $[27] !== outstandingBalance || $[28] !== patientDetail || $[29] !== sidebarCounts || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13) {
    t14 = {
      nextAppointment,
      isAppointmentsLoading: t10,
      patientDetail,
      isPatientDetailLoading: t11,
      sidebarCounts,
      isSidebarCountsLoading: t12,
      outstandingBalance,
      isChargesLoading: t13
    };
    $[26] = nextAppointment;
    $[27] = outstandingBalance;
    $[28] = patientDetail;
    $[29] = sidebarCounts;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}
function _temp4(s_0) {
  return s_0.provider;
}
function _temp3(s) {
  return s.service.name;
}
function _temp2(a, b) {
  return new Date(a.start).getTime() - new Date(b.start).getTime();
}
function _temp(sum, charge) {
  return sum + charge.totalOutstanding;
}